import React, {memo, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Image from 'next/image';
import type {StaticImageData} from 'next/image';
import Link from 'next/link';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import SectionHeader from '@/components/Common/SectionHeader';
import {borderRadius, borderWidth, colors, fontSize, fontWeight, gradients, lineHeight, overlay, shadows} from '@/styles/tokens';
import Eyebrow from '@/components/Common/Eyebrow';
import parse from 'html-react-parser';

export interface IndustryService {
    heading: string;
    /** Omit for a service with no corresponding page yet — it renders unlinked rather than 404ing. */
    redirect?: string;
}

export interface IndustryTab {
    title: string;
    desc: string;
    image: string | StaticImageData;
    imageAlt: string;
    /** Target of the "Know more" button — the industry's own service page. Omitted by
     *  the `overlay` variant, which renders no CTA. */
    link?: string;
    ctaText?: string;
    services: IndustryService[];
    /** Single-colour line icon for the tab (the plain `card` variant paints it as a mask). */
    icon?: string | StaticImageData;
}

// Panel height on desktop — the tab rail is capped to the same value so the two
// columns line up and any overflow scrolls. Tall enough to show 7 of the 11 tabs
// (56px each + 8px gaps + 24px rail padding) before the rail needs to scroll.
const PANEL_MIN_HEIGHT = 464;

// ─── Module-level sx constants ───────────────────────────────────────────────
// Sidebar on desktop (buttons stacked to the left of the panel), horizontal
// wrapping row on mobile where there isn't room for a side column.
const sectionSx = {
    width: '100%',
    background: colors.primary.deep
} as const;

const headingSx = {color: colors.text.inverse} as const;

const subheadingSx = {color: 'rgba(255,255,255,0.75)'} as const;

// Phones: the rail and the panel list dissolve into this row (`display: contents`), which takes over the
// rail's card look, and each panel lands under its tab through `order` (tab i = 2i, panel i = 2i + 1). So
// every panel is rendered once for both layouts. The xs values mirror tabsRowSx / overlayTabsRowSx.
const layoutRowSx = {
    display: 'flex',
    flexDirection: {xs: 'column', md: 'row'},
    alignItems: {xs: 'stretch', md: 'flex-start'},
    gap: {xs: 1, md: 3},
    width: {xs: '100%', md: 'auto'},
    p: {xs: 1.25, md: 0},
    borderRadius: {xs: borderRadius['3xl'], md: 0},
    background: {xs: colors.background.default, md: 'transparent'},
    boxShadow: {xs: shadows.elevated, md: 'none'},
    overflowY: {xs: 'auto', md: 'visible'}
} as const;

const overlayLayoutRowSx = {
    ...layoutRowSx,
    gap: {xs: 1.25, md: 3},
    p: {xs: 0.75, md: 0},
    mx: {xs: -0.75, md: 0},
    background: 'transparent',
    boxShadow: 'none'
} as const;

// Desktop rail is a white card fixed to the panel's height: the industries that fit
// alongside the image stay put and the rest scroll.
const tabsRowSx = {
    display: {xs: 'contents', md: 'flex'},
    flexDirection: 'column',
    gap: 1,
    flexShrink: 0,
    width: {xs: '100%', md: 300},
    p: {xs: 1.25, md: 1.5},
    borderRadius: borderRadius['3xl'],
    background: colors.background.default,
    boxShadow: shadows.elevated,
    maxHeight: {xs: 'none', md: 'none'},
    // Same value as the panel's minHeight, so the two columns are exactly level. A cap
    // alone left the rail short whenever the buttons came to less than the panel height.
    height: {xs: 'auto', md: PANEL_MIN_HEIGHT},
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: `${colors.primary.main} transparent`,
    '&::-webkit-scrollbar': {width: 2},
    '&::-webkit-scrollbar-track': {background: 'transparent'},
    '&::-webkit-scrollbar-thumb': {
        background: colors.primary.main,
        borderRadius: borderRadius.full
    },
    '&::-webkit-scrollbar-thumb:hover': {background: colors.primary.deep}
} as const;

const getTabSx = (isActive: boolean) =>
    ({
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        width: '100%',
        px: 1.5,
        py: 1.25,
        textAlign: 'left',
        cursor: 'pointer',
        border: 'none',
        borderRadius: borderRadius.xl,
        background: isActive ? colors.primary.deep : 'transparent',
        color: isActive ? colors.text.inverse : colors.text.primary,
        fontSize: fontSize[16],
        fontWeight: fontWeight.bold,
        lineHeight: lineHeight[24],
        transition: 'background-color 0.25s ease, color 0.25s ease',
        '&:hover': {
            background: isActive ? colors.primary.deep : colors.background.tag
        }
    } as const);

// Orange badge marks the active industry; the rest sit on the pale blue tile.
const getTabNumberSx = (isActive: boolean) =>
    ({
        flexShrink: 0,
        width: 36,
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: borderRadius.md,
        background: isActive ? colors.accent.orangeDark : colors.background.tag,
        color: isActive ? colors.text.inverse : colors.primary.deep,
        fontSize: fontSize[14],
        fontWeight: fontWeight.bold,
        lineHeight: lineHeight[20],
        transition: 'background-color 0.25s ease, color 0.25s ease'
    } as const);

const desktopPanelWrapSx = {display: {xs: 'contents', md: 'flex'}, flex: 1, minWidth: 0} as const;

// On phones the open panel sits 8px below its tab (on top of the row gap) and keeps its own height.
const panelPlacementSx = (index: number) => ({order: index * 2 + 1, flex: {xs: 'none', md: 1}, mt: {xs: 1, md: 0}} as const);

const panelSx = {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    flex: 1,
    borderRadius: borderRadius['3xl'],
    background: colors.background.missionCard,
    minHeight: {xs: 'auto', md: PANEL_MIN_HEIGHT},
    // The image is absolutely positioned on desktop, so the card is the only flex
    // child — centring it vertically balances the space above and below it.
    alignItems: 'center'
} as const;

// The artwork is the panel's ground at every width — on phones it used to sit as a
// separate 220px block above the card, which split the panel in two.
const imageWrapSx = {
    position: 'absolute',
    inset: 0,
    '& img': {objectFit: 'cover', objectPosition: 'center'}
} as const;

const glassCardSx = {
    position: 'relative',
    zIndex: 1,
    m: {xs: 2, md: 4},
    p: {xs: 2.5, md: 4},
    maxWidth: {xs: '100%', md: 560},
    borderRadius: borderRadius['3xl'],
    background: colors.background.default,
    boxShadow: shadows.elevated
} as const;

const industryTitleSx = {
    fontWeight: fontWeight.bold,
    fontSize: fontSize[22],
    lineHeight: lineHeight[32],
    color: colors.text.primary,
    mb: 1
} as const;

const descSx = {
    color: colors.text.primary,
    fontSize: fontSize[15],
    lineHeight: lineHeight[24],
    mb: 2.5
} as const;

const chipSx = {
    display: 'block',
    px: 1.75,
    py: 1,
    mb: 1.5,
    borderRadius: borderRadius.md,
    background: colors.background.tag,
    border: `${borderWidth.thin} solid transparent`,
    color: colors.text.primary,
    textDecoration: 'none',
    fontSize: fontSize[14],
    lineHeight: lineHeight[20],
    transition: 'border-color 0.2s ease, color 0.2s ease',
    '&:hover': {
        borderColor: colors.primary.main,
        color: colors.primary.deep,
        fontWeight: fontWeight.bold
    }
} as const;

// A service with no page yet: same tag look, no hover affordance since it isn't a link.
const chipStaticSx = {
    display: 'block',
    px: 1.75,
    py: 1,
    mb: 1.5,
    borderRadius: borderRadius.md,
    background: colors.background.tag,
    border: `${borderWidth.thin} solid transparent`,
    color: colors.text.primary,
    fontSize: fontSize[14],
    lineHeight: lineHeight[20]
} as const;

const ctaBtnSx = {
    mt: 1.5,
    px: 2.5,
    py: 1,
    borderRadius: borderRadius.md,
    background: colors.primary.navy,
    color: colors.text.inverse,
    fontSize: fontSize[14],
    fontWeight: fontWeight.bold,
    textTransform: 'none' as const,
    boxShadow: 'none',
    '&:hover': {background: colors.primary.deep, boxShadow: 'none'}
} as const;
// ─────────────────────────────────────────────────────────────────────────────

// `overlay` drops the numbered badges, the white glass card and the "Know more"
// button: the copy sits straight on the artwork with a progress bar and counter.
type IndustryTabsVariant = 'default' | 'overlay';

// Light-theme gradient ground, matching the pages' `lightGradient` sections.
const overlaySectionSx = {...sectionSx, background: gradients.lightBand} as const;
const overlayHeadingSx = {color: colors.text.primary} as const;
const overlaySubheadingSx = {color: colors.text.primary} as const;

// The rail loses its white card: each tab is its own white tile on the section
// ground, and only the active one is outlined.
// The rail scrolls, so a flush edge clipped the active tab's outline on the first and
// last tiles. Padding gives the outline room; the negative margin keeps the rail's
// visual width and its alignment with the panel unchanged.
const overlayTabsRowSx = {
    ...tabsRowSx,
    background: 'transparent',
    boxShadow: 'none',
    p: 0.75,
    mx: -0.75,
    gap: 1.25
} as const;

const getOverlayTabSx = (isActive: boolean) =>
    ({
        ...getTabSx(isActive),
        background: colors.background.default,
        borderRadius: borderRadius.lg,
        px: 2.5,
        py: 2,
        color: isActive ? colors.primary.deep : colors.text.primary,
        fontWeight: isActive ? fontWeight.bold : fontWeight.regular,
        border: `${borderWidth.thin} solid ${isActive ? colors.primary.deep : colors.neutral.borderLight}`,
        // The active tile also gets a thick left edge, so the selected industry reads
        // from the rail's left margin and not only from the thin outline.
        borderLeft: `${isActive ? '4px' : borderWidth.thin} solid ${isActive ? colors.primary.deep : colors.neutral.borderLight}`,
        transition: 'border-color 0.2s ease, border-left-width 0.2s ease, color 0.2s ease, background-color 0.2s ease',
        '&:hover': {background: colors.background.default, borderColor: colors.primary.deep}
    } as const);

// Two layers: the horizontal wash keeps the copy readable on the left, the bottom-up
// one covers the full-width footer (progress bar and counter) now that copy sits low.
const overlayScrimSx = {
    position: 'absolute',
    inset: 0,
    background: [
        `linear-gradient(0deg, ${overlay.dark80} 0%, ${overlay.dark45} 35%, transparent 65%)`,
        `linear-gradient(90deg, ${overlay.dark80} 0%, ${overlay.dark55} 60%, ${overlay.darkHover} 100%)`
    ].join(', ')
} as const;

// Content is pinned to the foot of the panel, leaving the artwork clear above it.
const overlayPanelSx = {...panelSx, alignItems: 'flex-end'} as const;

const overlayContentSx = {position: 'relative', zIndex: 1, width: '100%', p: {xs: 2.5, md: 5}} as const;

const overlayTitleSx = {
    color: colors.text.inverse,
    fontWeight: fontWeight.bold,
    fontSize: {xs: fontSize[28], md: fontSize[36]},
    mb: 1.5
} as const;

const overlayDescSx = {
    color: colors.text.inverse,
    fontSize: fontSize[14],
    lineHeight: 1.8,
    mb: 3,
    maxWidth: 640,
    // Inline links sit on the dark image scrim, so they keep the light copy colour.
    '& a': {color: 'inherit', textDecoration: 'underline'}
} as const;

const overlayChipRowSx = {display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4} as const;

const overlayChipSx = {
    px: 2,
    py: 1,
    borderRadius: borderRadius.full,
    border: `${borderWidth.thin} solid ${overlay.whiteBorder}`,
    background: overlay.whiteSubtle,
    color: colors.text.inverse,
    fontSize: fontSize[13],
    textDecoration: 'none'
} as const;

const overlayFooterSx = {display: 'flex', alignItems: 'center', gap: 2} as const;

const overlayTrackSx = {
    flex: 1,
    height: 3,
    borderRadius: borderRadius.full,
    background: overlay.whiteLight,
    overflow: 'hidden'
} as const;

const overlayFillSx = (percent: number) =>
    ({
        width: `${percent}%`,
        height: '100%',
        borderRadius: borderRadius.full,
        background: colors.accent.orange,
        transition: 'width 0.3s ease'
    } as const);

const overlayCounterSx = {color: overlay.whiteSoft, fontSize: fontSize[12]} as const;

interface HomeIndustryTabsProps {
    eyebrow?: string;
    heading: string;
    subheading: string;
    data: IndustryTab[];
    variant?: IndustryTabsVariant;
    /** Header alignment. Defaults to left for `overlay`, centre otherwise. */
    headerAlign?: 'left' | 'center';
}

const HomeIndustryTabs = ({eyebrow, heading, subheading, data, variant = 'default', headerAlign}: HomeIndustryTabsProps) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const isOverlay = variant === 'overlay';

    const renderOverlayPanel = (item: IndustryTab, index: number) => (
        <Box sx={{...overlayPanelSx, ...panelPlacementSx(index), display: activeIndex === index ? 'flex' : 'none'}}>
            <Box sx={imageWrapSx}>
                <Image src={item.image} alt={item.imageAlt} fill quality={90} sizes="(max-width: 900px) 100vw, 1200px" />
            </Box>
            <Box sx={overlayScrimSx} />

            <Box sx={overlayContentSx}>
                <Typography component="h3" sx={overlayTitleSx} className="font-headers">
                    {item.title}
                </Typography>
                <Typography component="p" sx={overlayDescSx} className="font-regular-text">
                    {parse(item.desc)}
                </Typography>

                <Box sx={overlayChipRowSx}>
                    {item.services.map((service) => (
                        <Typography component="span" key={service.heading} sx={overlayChipSx} className="font-regular-text">
                            {service.heading}
                        </Typography>
                    ))}
                </Box>

                <Box sx={overlayFooterSx}>
                    <Box sx={overlayTrackSx}>
                        <Box sx={overlayFillSx(((index + 1) / data.length) * 100)} />
                    </Box>
                    <Typography component="span" sx={overlayCounterSx} className="font-headers">
                        {String(index + 1).padStart(2, '0')} / {String(data.length).padStart(2, '0')}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );

    const renderDefaultPanel = (item: IndustryTab, index: number) => (
        <Box sx={{...panelSx, ...panelPlacementSx(index), display: activeIndex === index ? 'flex' : 'none'}}>
            <Box sx={imageWrapSx}>
                <Image src={item.image} alt={item.imageAlt} fill quality={90} sizes="(max-width: 900px) 100vw, 1200px" />
            </Box>

            <Box sx={glassCardSx}>
                <Typography component="h3" sx={industryTitleSx} className="font-headers">
                    {item.title}
                </Typography>
                <Typography component="p" sx={descSx} className="font-regular-text">
                    {parse(item.desc)}
                </Typography>

                <Grid container columnSpacing={1.5}>
                    {item.services.map((service) =>
                        service.redirect ? (
                            <Grid item xs={12} sm={6} key={service.heading}>
                                <Link href={service.redirect} target="_blank" className="remove-underline" style={{textDecoration: 'none'}}>
                                    <Typography component="span" sx={chipSx} className="font-regular-text">
                                        {service.heading}
                                    </Typography>
                                </Link>
                            </Grid>
                        ) : (
                            <Grid item xs={12} sm={6} key={service.heading}>
                                <Typography component="span" sx={chipStaticSx} className="font-regular-text">
                                    {service.heading}
                                </Typography>
                            </Grid>
                        )
                    )}
                </Grid>

                {item.link && (
                    <Link href={item.link} target="_blank" className="remove-underline" style={{textDecoration: 'none'}}>
                        <MUIButton
                            label={item.ctaText ?? 'Know more'}
                            variant="contained"
                            size="small"
                            endIcon={<ArrowForwardIcon />}
                            disableRipple
                            className="moving-icon-button font-headers"
                            sx={ctaBtnSx}
                        />
                    </Link>
                )}
            </Box>
        </Box>
    );

    const renderPanel = isOverlay ? renderOverlayPanel : renderDefaultPanel;

    return (
        // The spacing classes live on the section root, not on a wrapper: the root paints
        // the section's own background, so padding on an outer element would only add page
        // background around the band and leave the content flush with its edges.
        <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding" sx={isOverlay ? overlaySectionSx : sectionSx}>
            <MUIContainer>
                <SectionHeader
                    eyebrow={eyebrow ? <Eyebrow text={eyebrow} onDark={!isOverlay} /> : eyebrow}
                    heading={heading ? parse(heading) : ''}
                    subheading={subheading ? parse(subheading) : ''}
                    align={headerAlign ?? (isOverlay ? 'left' : 'center')}
                    headingSx={isOverlay ? overlayHeadingSx : headingSx}
                    subheadingSx={isOverlay ? overlaySubheadingSx : subheadingSx}
                />

                <Box sx={isOverlay ? overlayLayoutRowSx : layoutRowSx}>
                    <Box sx={isOverlay ? overlayTabsRowSx : tabsRowSx}>
                        {data.map((item, index) => (
                            <Box
                                key={item.title}
                                component="button"
                                type="button"
                                aria-pressed={activeIndex === index}
                                onClick={() => setActiveIndex(index)}
                                sx={{
                                    ...(isOverlay ? getOverlayTabSx(activeIndex === index) : getTabSx(activeIndex === index)),
                                    order: index * 2
                                }}
                                className="font-headers"
                            >
                                {!isOverlay && (
                                    <Box component="span" sx={getTabNumberSx(activeIndex === index)}>
                                        {String(index + 1).padStart(2, '0')}
                                    </Box>
                                )}
                                {item.title}
                            </Box>
                        ))}
                    </Box>

                    {/* All panels are rendered once, so every industry's copy and service links sit in the
                        server HTML for crawlers; only the active one is shown. On phones layoutRowSx dissolves
                        this wrapper and the rail so the open panel lands under its tab. */}
                    <Box sx={desktopPanelWrapSx}>
                        {data.map((item, index) => (
                            <React.Fragment key={item.title}>{renderPanel(item, index)}</React.Fragment>
                        ))}
                    </Box>
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(HomeIndustryTabs);
